import { CheckableSelectItem, DropdownSelect } from '@dixa/lib-shared-habla';
import { useCallback, useMemo, useState } from 'react';

import { ConversationEventType } from '../activityLogFilters.types';
import { conversationEventTypeLabels } from '../utils/eventTypeLabels';

type EventTypeItem = {
  readonly id: ConversationEventType;
  readonly name: string;
};

/** All selectable event type items, sorted alphabetically by their label. */
const eventTypeItems: readonly EventTypeItem[] = (
  Object.keys(conversationEventTypeLabels) as ConversationEventType[]
)
  .map((id) => ({ id, name: conversationEventTypeLabels[id] }))
  .sort((first, second) => first.name.localeCompare(second.name));

type ActivityLogEventTypeFilterProps = {
  /** The currently selected event types. */
  readonly value: readonly ConversationEventType[];
  /** Called when the selected event types change. */
  readonly onChange: (eventTypes: ConversationEventType[]) => void;
};

/** Represents the component for filtering the activity log by event type. */
export const ActivityLogEventTypeFilter = ({
  value,
  onChange,
}: ActivityLogEventTypeFilterProps): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState<string>();

  const items = useMemo<readonly EventTypeItem[]>(() => {
    if (!searchTerm) {
      return eventTypeItems;
    }

    const term = searchTerm.toLowerCase();
    return eventTypeItems.filter(({ name }) =>
      name.toLowerCase().includes(term),
    );
  }, [searchTerm]);

  const handleChange = useCallback(
    (item: EventTypeItem, checked: boolean) => {
      onChange(
        checked ? [...value, item.id] : value.filter((id) => id !== item.id),
      );
    },
    [onChange, value],
  );

  const handleClear = useCallback(() => onChange([]), [onChange]);

  const handleSearch = useCallback((search?: string) => {
    setSearchTerm(search?.trim() || undefined);
  }, []);

  const handleToggle = useCallback((isOpen: boolean) => {
    // Reset the search when the dropdown closes so it opens fresh next time.
    if (!isOpen) {
      setSearchTerm(undefined);
    }
  }, []);

  return (
    <DropdownSelect
      keepOpen
      hasSearch
      noArrow={false}
      items={items as EventTypeItem[]}
      selectedItems={value as ConversationEventType[]}
      itemComponent={CheckableSelectItem}
      buttonProps={{ variant: 'tertiary', label: value.length || undefined }}
      dropdownProps={{ style: { minWidth: 280 } }}
      itemsDisplayCount={8}
      onChange={handleChange}
      onClear={handleClear}
      onSearch={handleSearch}
      onToggle={handleToggle}
    >
      Event types
    </DropdownSelect>
  );
};
